import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Badge,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { Add, Remove, Notifications } from '@mui/icons-material';
import { getMerchandiseDetails, searchMerchandise } from '../../services/api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflow: 'auto'
};

const SPECIAL_REFERENCES = new Set(['ATB1882490', 'ATC18824115']);
const SPECIAL_BARCODES = new Set(['560000002710', '560000002704']);
const SPECIAL_DESCRIPTIONS = new Set([
  'SACODEPAPELKRAFTBRANCOCOMASATORCIDA18X8X2490GR',
  'SACODEPAPELKRAFTCASTANHOCOMASATORCIDA18X8X24115GR'
]);
const SPECIAL_QUANTITY_OPTIONS = [250, 500];

const normalizeDescriptionKey = (value) =>
  (value || '')
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const isOpBarcodeProduct = (item) =>
  item?.sourceType === 'op_barcode' || item?.source_type === 'op_barcode';

const getItemQuantityPerBox = (item) =>
  Number(item?.quantityPerBox ?? item?.quantity_per_box ?? 0);

const getOpDisplayName = (item) =>
  item?.displayName ||
  item?.display_name ||
  item?.name ||
  `OP ${item?.opId || item?.op_id || ''}`.trim();

const collapseDuplicatedScan = (value) => {
  const normalizedValue = (value || '').toString().trim();

  if (normalizedValue.length < 2 || normalizedValue.length % 2 !== 0) {
    return normalizedValue;
  }

  const halfLength = normalizedValue.length / 2;
  const firstHalf = normalizedValue.slice(0, halfLength);
  const secondHalf = normalizedValue.slice(halfLength);

  return firstHalf === secondHalf ? firstHalf : normalizedValue;
};

const normalizeSearchTerm = (value) =>
  collapseDuplicatedScan((value || '').toString().replace(/\s+/g, ' ').trim());

const isLikelyOpBarcode = (value) => {
  const normalizedValue = normalizeSearchTerm(value);

  return (
    /^\d+\s*\|\s*[0-9]+(?:[.,][0-9]+)?\s*\|\s*.+$/.test(normalizedValue) ||
    /^\d+\s*[xX]\s*[0-9]+(?:[.,][0-9]+)?\s*[xX]\s*.+$/.test(normalizedValue)
  );
};

const isSpecialQuantityProduct = ({ reference, name, barcode }) => {
  const normalizedReference = (reference || '').toString().trim().toUpperCase();
  const normalizedReferenceDescription = normalizeDescriptionKey(reference);
  const normalizedNameDescription = normalizeDescriptionKey(name);
  const normalizedBarcode = (barcode || '').toString().trim();

  return (
    SPECIAL_REFERENCES.has(normalizedReference) ||
    SPECIAL_DESCRIPTIONS.has(normalizedReferenceDescription) ||
    SPECIAL_DESCRIPTIONS.has(normalizedNameDescription) ||
    SPECIAL_BARCODES.has(normalizedBarcode)
  );
};

const getNormalizedQuantityPerBox = (article) => {
  const quantityPerBox = Number(article.quantity_per_box ?? article.quantityPerBox ?? 0);

  if (!isSpecialQuantityProduct(article)) {
    return quantityPerBox;
  }

  return SPECIAL_QUANTITY_OPTIONS.includes(quantityPerBox) ? quantityPerBox : 500;
};

const normalizeSpecialProduct = (item) => {
  if (!isSpecialQuantityProduct(item)) {
    return item;
  }

  const quantityPerBox = getNormalizedQuantityPerBox(item);
  const boxes = Number(item.boxes ?? 0);

  return {
    ...item,
    quantityPerBox,
    totalQuantity: boxes * quantityPerBox
  };
};

const getProductKey = (item) => {
  if (isOpBarcodeProduct(item)) {
    return `op:${item.opId || item.op_id || ''}:${item.stockReference || item.stock_reference || item.reference || ''}:${getOpDisplayName(item)}:${getItemQuantityPerBox(item)}`;
  }

  return item.groupKey || item.group_key || item.reference;
};

const ListSaida = ({ open, onClose, onSave, initialData }) => {
  const [products, setProducts] = useState(initialData || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [highlightedArticle, setHighlightedArticle] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  const resetSearchState = () => {
    setSearchTerm('');
    setSelectedArticle(null);
    setHighlightedArticle(null);
    setSearchResults([]);
  };

  useEffect(() => {
    setProducts((initialData || []).map(normalizeSpecialProduct));
    setSearchTerm('');
    setSelectedArticle(null);
    setHighlightedArticle(null);
    setSearchResults([]);
  }, [initialData, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const query = normalizeSearchTerm(searchTerm);

    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    let isActive = true;

    const debounceId = setTimeout(async () => {
      setSearchLoading(true);

      try {
        const response = await searchMerchandise(query);

        if (isActive) {
          setSearchResults(response.data || []);
        }
      } catch (error) {
        console.error('Erro ao pesquisar artigos:', error);

        if (isActive) {
          setSearchResults([]);
        }
      } finally {
        if (isActive) {
          setSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(debounceId);
    };
  }, [open, searchTerm]);

  const addProduct = (article) => {
    const quantityPerBox = getNormalizedQuantityPerBox(article);
    const groupKey = getProductKey(article);

    if (!article.reference || !quantityPerBox) {
      setErrorCount(prev => prev + 1);
      return;
    }

    setProducts(prevProducts => {
      const existingProductIndex = prevProducts.findIndex(
        item => getProductKey(item) === groupKey
      );

      if (existingProductIndex !== -1) {
        const updatedProducts = [...prevProducts];
        const existingProduct = updatedProducts[existingProductIndex];
        const newBoxes = existingProduct.boxes + 1;

        updatedProducts[existingProductIndex] = {
          ...existingProduct,
          name: existingProduct.name || article.name,
          displayName: existingProduct.displayName || article.display_name || article.name,
          barcode: existingProduct.barcode || article.barcode || '',
          boxes: newBoxes,
          totalQuantity: newBoxes * existingProduct.quantityPerBox
        };

        return updatedProducts;
      }

      return [
        ...prevProducts,
        {
          groupKey,
          reference: article.reference,
          stockReference: article.stock_reference || article.reference,
          name: article.name,
          displayName: article.display_name || article.name,
          totalQuantity: quantityPerBox,
          quantityPerBox,
          boxes: 1,
          barcode: article.barcode || '',
          sourceType: article.source_type || '',
          opId: article.op_id || null,
          measure: article.measure || '',
          excelMerchandiseName: article.excel_merchandise_name || ''
        }
      ];
    });
  };

  const handleArticleSelect = (_, article) => {
    if (!article || typeof article === 'string') {
      return;
    }

    addProduct(article);
    resetSearchState();
  };

  const findExactMatch = (term, options) => {
    const normalizedTerm = term.trim().toLowerCase();

    return options.find(option => {
      const values = [option.name, option.reference, option.barcode];

      return values.some(value => (value || '').toString().trim().toLowerCase() === normalizedTerm);
    });
  };

  const handleInputSubmit = async () => {
    const term = normalizeSearchTerm(searchTerm);

    if (!term) {
      return;
    }

    if (term !== searchTerm) {
      setSearchTerm(term);
    }

    if (highlightedArticle) {
      addProduct(highlightedArticle);
      resetSearchState();
      return;
    }

    setSearchLoading(true);

    try {
      if (!isLikelyOpBarcode(term)) {
        try {
          const response = await getMerchandiseDetails(term);

          addProduct({
            ...response.data,
            barcode: response.data.barcode || term
          });
          resetSearchState();
          return;
        } catch (error) {
          const isNotFound = error.response && error.response.status === 404;

          if (!isNotFound) {
            console.error('Erro ao procurar artigo por código de barras:', error);
            setErrorCount(prev => prev + 1);
            return;
          }
        }
      }

      const localExactMatch = findExactMatch(term, searchResults);

      if (localExactMatch) {
        addProduct(localExactMatch);
        resetSearchState();
        return;
      }

      const response = await searchMerchandise(term);
      const results = response.data || [];
      const exactMatch = findExactMatch(term, results);

      setSearchResults(results);

      if (exactMatch) {
        addProduct(exactMatch);
        resetSearchState();
        return;
      }

      if (results.length === 1) {
        addProduct(results[0]);
        resetSearchState();
        return;
      }

      setErrorCount(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao pesquisar artigos:', error);
      setErrorCount(prev => prev + 1);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleInputKeyDown = async (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    await handleInputSubmit();
  };

  const updateBoxes = (productKey, delta) => {
    setProducts(prevProducts => {
      const newProducts = prevProducts.map(item => {
        if (getProductKey(item) === productKey) {
          const newBoxes = item.boxes + delta;
          const finalBoxes = newBoxes < 0 ? 0 : newBoxes;
          
          return {
            ...item,
            boxes: finalBoxes,
            totalQuantity: finalBoxes * item.quantityPerBox
          };
        }
        return item;
      });
      
      return newProducts.filter(item => item.boxes > 0);
    });
  };

  const updateQuantityPerBox = (productKey, newQuantityPerBox) => {
    const quantityPerBox = Number(newQuantityPerBox);

    setProducts(prevProducts => 
      prevProducts.map(item => {
        if (getProductKey(item) === productKey) {
          return {
            ...item,
            quantityPerBox,
            totalQuantity: item.boxes * quantityPerBox
          };
        }
        return item;
      })
    );
  };

  const handleSave = () => {
    onSave(products);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Ficha de Obras
          </Typography>
          <IconButton size="large">
            <Badge badgeContent={errorCount} color="error">
              <Notifications fontSize="large" />
            </Badge>
          </IconButton>
        </Box>
        <Autocomplete
          fullWidth
          autoHighlight
          options={searchResults}
          value={selectedArticle}
          inputValue={searchTerm}
          loading={searchLoading}
          filterOptions={(options) => options}
          onHighlightChange={(_, option) => setHighlightedArticle(option)}
          isOptionEqualToValue={(option, value) => option.reference === value.reference}
          getOptionLabel={(option) => {
            if (!option) {
              return '';
            }

            const name = option.name || '';
            const reference = option.reference || '';
            return `${name} (${reference})`;
          }}
          onInputChange={(_, value, reason) => {
            setHighlightedArticle(null);

            if (reason === 'reset') {
              setSearchTerm('');
              return;
            }

            setSearchTerm(normalizeSearchTerm(value));
          }}
          onChange={handleArticleSelect}
          noOptionsText={
            searchTerm.trim().length < 2
              ? 'Escreva pelo menos 2 caracteres'
              : 'Nenhum artigo encontrado'
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.reference} | {option.quantity_per_box}/caixa
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Pesquise por nome, referência ou código de barras"
              sx={{ mb: 2 }}
              onKeyDown={handleInputKeyDown}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        {products.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artigo</TableCell>
                  <TableCell>Quantidade</TableCell>
                  <TableCell>Caixas</TableCell>
                  <TableCell>Qtd/caixa</TableCell>
                  <TableCell>Ajuste</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((item) => (
                  <TableRow key={getProductKey(item)}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.displayName || item.name || item.reference}
                      </Typography>
                      {isOpBarcodeProduct(item) && item.reference ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ref. stock: {item.reference}
                        </Typography>
                      ) : null}
                      {isOpBarcodeProduct(item) ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Qtd/caixa OP: {item.quantityPerBox}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{item.totalQuantity}</TableCell>
                    <TableCell>{item.boxes}</TableCell>
                    <TableCell>
                      {isSpecialQuantityProduct(item) ? (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={item.quantityPerBox}
                            onChange={(e) => updateQuantityPerBox(getProductKey(item), e.target.value)}
                            sx={{
                              color: 'white',
                              backgroundColor: 'error.main',
                              '& .MuiSelect-icon': {
                                color: 'white'
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'error.main'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'error.dark'
                              }
                            }}
                          >
                            {SPECIAL_QUANTITY_OPTIONS.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        item.quantityPerBox
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => updateBoxes(getProductKey(item), -1)}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => updateBoxes(getProductKey(item), +1)}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={handleSave}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ListSaida;
